import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Send, Loader2, CheckCircle2, Inbox } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const field = "w-full rounded-2xl border border-slate-200 bg-surface-deep/60 px-4 py-3 text-sm text-white placeholder:text-slate-400 outline-none focus:border-gold-400";

const ContactRow = ({ c, reload }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const open = (c.status || (c.handled ? "closed" : "open")) === "open";
  const reply = async () => { setBusy(true); try { await api.post(`/admin/contact/${c.contact_id}/reply`, { text }); setText(""); toast.success("Reply sent by email and in the app"); reload(); } catch (e) { toast.error(formatApiError(e)); } setBusy(false); };
  const status = async () => { await api.put(`/admin/contact/${c.contact_id}`, { status: open ? "closed" : "open" }).catch(() => {}); reload(); };
  return (
    <li data-testid={`contact-row-${c.contact_id}`} className={`rounded-2xl border p-3 ${open ? "border-gold-400/50 bg-gold-400/5" : "border-slate-200"}`}>
      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500"><span className="rounded-full bg-navy-900 px-2 py-0.5 font-bold text-white">{c.category || "Help"}</span><b className="text-slate-900 text-sm">{c.subject}</b><span>{c.name || c.email} · {c.email}</span><span>{new Date(c.ts).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}</span>
        <button data-testid={`contact-status-${c.contact_id}`} onClick={status} className="ml-auto rounded-full border border-slate-300 px-2.5 py-1 font-semibold text-slate-700">{open ? "Close" : "Reopen"}</button></div>
      <p className="mt-1.5 text-sm text-slate-800 whitespace-pre-wrap">{c.message}</p>
      {(c.replies ?? []).map((r, i) => <p key={i} className="mt-2 rounded-xl bg-surface-2 p-2 text-sm text-slate-700"><b className="text-gold-300">{r.by}:</b> {r.text}</p>)}
      <div className="mt-2 flex gap-2">
        <input data-testid={`contact-reply-input-${c.contact_id}`} value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a reply" className="flex-1 min-w-0 h-9 rounded-xl border border-slate-200 bg-surface px-3 text-sm text-slate-900 outline-none focus:border-gold-400" />
        <button data-testid={`contact-reply-send-${c.contact_id}`} onClick={reply} disabled={busy || !text.trim()} className="h-9 rounded-xl bg-gold-400 px-3 text-xs font-bold text-black disabled:opacity-40">Reply</button>
      </div>
    </li>
  );
};

export const ContactInbox = () => {
  const [list, setList] = useState(null);
  const load = () => api.get("/admin/contact").then((r) => setList(Array.isArray(r.data) ? r.data : [])).catch(() => setList([]));
  useEffect(() => { load(); }, []);
  if (!list) return null;
  return (
    <section data-testid="admin-contact-inbox" className="card p-5 sm:p-6">
      <p className="eyebrow flex items-center gap-2"><Inbox className="w-4 h-4" />Contact messages</p>
      <h2 className="mt-1 font-display text-3xl text-white">From the contact page · {list.filter((c) => (c.status || (c.handled ? "closed" : "open")) === "open").length} open</h2>
      <ul className="mt-4 space-y-2 max-h-96 overflow-y-auto">
        {list.map((c) => <ContactRow key={c.contact_id} c={c} reload={load} />)}
        {!list.length && <li className="text-sm text-slate-500">No messages yet.</li>}
      </ul>
    </section>
  );
};

export default function Contact() {
  const { user } = useAuth();
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [category, setCategory] = useState("Help");
  const [mine, setMine] = useState([]);
  const loadMine = () => api.get("/contact/mine").then((r) => setMine(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  useEffect(() => { loadMine(); }, []);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/contact", { subject, message, category }); setSent(true); setSubject(""); setMessage(""); loadMine(); }
    catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };
  return (
    <main data-testid="contact-page" className="mx-auto max-w-2xl px-4 sm:px-6 py-8 sm:py-12 space-y-6">
      <div>
        <p className="eyebrow flex items-center gap-2"><Mail className="w-4 h-4" />Contact</p>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-none">Get in touch</h1>
        <p className="mt-3 text-slate-600">Found a bug, got an idea, or need help? Send a message and it goes straight to the Daybreyk team by email.</p>
      </div>
      {sent ? (
        <section data-testid="contact-sent" className="card p-8 text-center animate-rise">
          <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-400" />
          <h2 className="mt-3 font-display text-3xl text-white">Message sent</h2>
          <p className="mt-1 text-slate-600">Thanks. We'll reply to {user?.email}.</p>
          <button data-testid="contact-another" onClick={() => setSent(false)} className="mt-5 rounded-full border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-700 hover:text-white">Send another</button>
        </section>
      ) : (
        <form onSubmit={send} data-testid="contact-form" className="card p-5 sm:p-6 space-y-3">
          <p className="text-sm text-slate-500">Replying to <b className="text-white">{user?.email}</b></p>
          <div className="flex flex-wrap gap-2">{["Help", "Bug", "Idea", "Safety"].map((k) => <button type="button" key={k} data-testid={`contact-category-${k.toLowerCase()}`} onClick={() => setCategory(k)} className={`rounded-full px-4 py-1.5 text-sm font-semibold ${category === k ? "bg-gold-400 text-black" : "border border-slate-200 text-slate-600"}`}>{k}</button>)}</div>
          <input data-testid="contact-subject-input" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={120} placeholder="Subject" className={field} />
          <textarea data-testid="contact-message-input" value={message} onChange={(e) => setMessage(e.target.value)} rows={7} maxLength={4000} placeholder="Your message" className={field} />
          <button data-testid="contact-submit" disabled={busy || !subject.trim() || message.trim().length < 5} className="inline-flex items-center gap-2 rounded-full bg-gold-400 px-6 py-3 text-sm font-bold text-black hover:bg-gold-300 disabled:opacity-40">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}Send message
          </button>
        </form>
      )}
      {mine.length > 0 && (
        <section data-testid="contact-mine" className="space-y-2">
          <p className="eyebrow">Your messages</p>
          {mine.map((c) => (
            <div key={c.contact_id} className="card !rounded-2xl p-4">
              <p className="text-xs text-slate-500"><b className="text-slate-900 text-sm">{c.subject}</b> · {c.category || "Help"} · {(c.status || (c.handled ? "closed" : "open")) === "open" ? "Waiting for a reply" : "Answered"}</p>
              <p className="mt-1 text-sm text-slate-700 whitespace-pre-wrap">{c.message}</p>
              {(c.replies ?? []).map((r, i) => <p key={i} className="mt-2 rounded-xl border border-gold-400/40 bg-gold-400/10 p-2.5 text-sm text-slate-800"><b>{r.by} from Daybreyk:</b> {r.text}</p>)}
            </div>
          ))}
        </section>
      )}
    </main>
  );
}
